import React from "react";
import {
  Box,
  ButtonBase,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import FolderSpecialRoundedIcon from "@mui/icons-material/FolderSpecialRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import WorkspacesRoundedIcon from "@mui/icons-material/WorkspacesRounded";
import BugReportRoundedIcon from "@mui/icons-material/BugReportRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { useLocation, useNavigate } from "react-router-dom";

const primary = [
  { value: "/dashboard/owner", label: "Hoy", icon: <DashboardRoundedIcon /> },
  { value: "/dashboard/owner/calendario", label: "Agenda", icon: <CalendarMonthRoundedIcon /> },
  { value: "/dashboard/owner/proyectos", label: "Proyectos", icon: <FolderSpecialRoundedIcon /> },
  { value: "/dashboard/owner/finanzas", label: "Dinero", icon: <AccountBalanceWalletRoundedIcon /> },
  { value: "/dashboard/owner/clientes", label: "Personas", icon: <GroupsRoundedIcon /> },
];

const secondary = [
  { value: "/dashboard/owner/habitos", label: "Hábitos", icon: <TrackChangesRoundedIcon fontSize="small" /> },
  { value: "/dashboard/owner/movimientos", label: "Movimientos", icon: <ReceiptLongRoundedIcon fontSize="small" /> },
  { value: "/dashboard/owner/operacion", label: "Operación", icon: <WorkspacesRoundedIcon fontSize="small" /> },
  { value: "/dashboard/owner/estadisticas", label: "Estadísticas", icon: <QueryStatsRoundedIcon fontSize="small" /> },
  { value: "/dashboard/owner/uso-ia", label: "Uso de IA", icon: <AutoAwesomeRoundedIcon fontSize="small" /> },
  { value: "/dashboard/owner/jarvis-lab", label: "Jarvis Lab", icon: <BugReportRoundedIcon fontSize="small" /> },
];

function activePath(pathname) {
  if (pathname.startsWith("/dashboard/owner/habitos")) return "/dashboard/owner/habitos";
  if (pathname.startsWith("/dashboard/owner/jarvis-lab")) return "/dashboard/owner/jarvis-lab";
  if (pathname.startsWith("/dashboard/owner/uso-ia")) return "/dashboard/owner/uso-ia";
  if (pathname.startsWith("/dashboard/owner/estadisticas")) return "/dashboard/owner/estadisticas";
  if (pathname.startsWith("/dashboard/owner/movimientos")) return "/dashboard/owner/movimientos";
  if (pathname.startsWith("/dashboard/owner/finanzas")) return "/dashboard/owner/finanzas";
  if (pathname.startsWith("/dashboard/owner/proyectos")) return "/dashboard/owner/proyectos";
  if (pathname.startsWith("/dashboard/owner/calendario")) return "/dashboard/owner/calendario";
  if (pathname.startsWith("/dashboard/owner/clientes")) return "/dashboard/owner/clientes";
  if (pathname.startsWith("/dashboard/owner/operacion")) return "/dashboard/owner/operacion";
  return "/dashboard/owner";
}

export default function OwnerWorkspaceNavigation() {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const navigate = useNavigate();
  const current = activePath(location.pathname);
  const [anchor, setAnchor] = React.useState(null);
  const secondaryActive = secondary.some((item) => item.value === current);

  const go = (path) => { setAnchor(null); navigate(path); };

  return (
    <>
      <Box
        component="nav"
        aria-label="Navegación Owner 360"
        sx={{
          display: { xs: "none", sm: "flex" },
          width: { sm: 56 },
          flex: "0 0 auto",
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          px: .45,
          py: .6,
          flexDirection: "column",
          alignItems: "center",
          gap: .45,
          overflowY: "auto",
          overscrollBehavior: "contain",
        }}
      >
        {primary.map((tab) => {
          const active = current === tab.value;
          return (
            <Tooltip key={tab.value} title={tab.label} placement="right" arrow enterDelay={250}>
              <IconButton
                type="button"
                aria-label={`Ir a ${tab.label}`}
                aria-current={active ? "page" : undefined}
                onClick={() => go(tab.value)}
                sx={{
                  width: 46,
                  height: 46,
                  borderRadius: 2.5,
                  color: active ? "primary.main" : "text.secondary",
                  bgcolor: active ? "rgba(243,90,58,.11)" : "transparent",
                  border: active ? "1px solid rgba(243,90,58,.2)" : "1px solid transparent",
                  "& svg": { fontSize: 23 },
                }}
              >
                {tab.icon}
              </IconButton>
            </Tooltip>
          );
        })}

        <Tooltip title="Más" placement="right" arrow>
          <IconButton
            aria-label="Más secciones"
            onClick={(event) => setAnchor(event.currentTarget)}
            sx={{ width: 46, height: 46, borderRadius: 2.5, color: secondaryActive ? "primary.main" : "text.secondary", bgcolor: secondaryActive ? "rgba(243,90,58,.11)" : "transparent" }}
          >
            <MoreHorizRoundedIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Box
        component="nav"
        aria-label="Navegación móvil Owner 360"
        sx={{
          display: { xs: "grid", sm: "none" },
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          gridTemplateColumns: "repeat(6,minmax(0,1fr))",
          px: "max(4px, env(safe-area-inset-left))",
          pt: .45,
          pb: "max(5px, env(safe-area-inset-bottom))",
          minHeight: "calc(62px + env(safe-area-inset-bottom))",
          borderTop: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(var(--wp-panel-surface-rgb,255,255,255),.97)",
          boxShadow: "0 -8px 26px rgba(var(--wp-panel-ink-rgb,25,36,47),.08)",
          backdropFilter: "blur(14px)",
        }}
      >
        {primary.map((tab) => {
          const active = current === tab.value;
          return (
            <ButtonBase
              key={tab.value}
              aria-label={`Ir a ${tab.label}`}
              aria-current={active ? "page" : undefined}
              onClick={() => go(tab.value)}
              sx={{
                minWidth: 0,
                minHeight: 54,
                borderRadius: 2,
                mx: .15,
                color: active ? "primary.main" : "text.secondary",
                bgcolor: active ? "rgba(243,90,58,.09)" : "transparent",
                display: "flex",
                flexDirection: "column",
                gap: .1,
                "& svg": { fontSize: 22 },
              }}
            >
              {tab.icon}
              <Typography component="span" sx={{ fontSize: 9.5, lineHeight: 1.1, fontWeight: active ? 1000 : 800, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
                {tab.label}
              </Typography>
            </ButtonBase>
          );
        })}
        <ButtonBase
          aria-label="Más secciones"
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{
            minWidth: 0,
            minHeight: 54,
            borderRadius: 2,
            mx: .15,
            color: secondaryActive ? "primary.main" : "text.secondary",
            bgcolor: secondaryActive ? "rgba(243,90,58,.09)" : "transparent",
            display: "flex",
            flexDirection: "column",
            gap: .1,
            "& svg": { fontSize: 22 },
          }}
        >
          <MoreHorizRoundedIcon />
          <Typography component="span" sx={{ fontSize: 9.5, lineHeight: 1.1, fontWeight: secondaryActive ? 1000 : 800 }}>Más</Typography>
        </ButtonBase>
      </Box>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={mobile ? { vertical: "top", horizontal: "right" } : { vertical: "bottom", horizontal: "right" }}
        transformOrigin={mobile ? { vertical: "bottom", horizontal: "right" } : { vertical: "bottom", horizontal: "left" }}
        PaperProps={{ sx: mobile ? { mb: 1, minWidth: 210, borderRadius: 3 } : { minWidth: 180 } }}
      >
        {secondary.map((item) => (
          <MenuItem key={item.value} selected={current === item.value} onClick={() => go(item.value)} sx={{ minWidth: 180, py: 1 }}>
            <ListItemIcon>{item.icon}</ListItemIcon>{item.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
